import * as _ from 'lodash';

import {
  CONDITIONS_TYPES,
  CUSTOM_FIELD_TYPES,
  SIMPLE_CONDITIONS_OPERATORS,
  SimpleConditions,
  SimpleConditionsLeaf,
  SimpleConditionsOperator,
  SimpleConditionsWrapper,
} from '../../models';
import { ApiService } from '../../services/api.service';
import { CustomFieldsService } from '../../services/custom-fields.service';
import { DATE_OPTION_LABELS, PRIVATE_CHANNEL_NAME } from '../constants';

export const FILTER_OPERATORS = {
  EQUALS: 'eq',
  NOT_EQUALS: 'ne',
} as const;

export type FilterOperator = (typeof FILTER_OPERATORS)[keyof typeof FILTER_OPERATORS];

export const CURRENT_USER_SENTINEL = '__CURRENT_USER__';

export interface FilterValue {
  label: string;
  id: string | boolean;
  additionalFields?: any; // When the filter type is a custom field, this will contain additional information used to further filter
  values?: any;
}

export interface FilterOption {
  label: string;
  id: string;
  searchId?: string;
  icon?: string;
  // TODO: Add support for text input type
  type: 'select' | 'date' | 'variable';
  grouping?: 'or' | 'and';
  values?: FilterValue[] | (() => Promise<FilterValue[]>); // Only for type 'select'
  searchablePopoverOptions?: any;
  lookupId?: string;
  searchablePopover?: string;
  operator?: FilterOperator;
}

export type SelectedFilterMap = Map<FilterOption, FilterValue[]>;

/**
 * Converts a SelectedFilterMap to an array of filter clauses with operators.
 * This is used when sending filters to the backend in the new format.
 *
 * @param uiFilters The SelectedFilterMap from the UI
 * @returns Array of filter clauses with field, operator, and value
 */
export function convertFiltersToReportFilterClauses(
  uiFilters: SelectedFilterMap,
): { field: string; operator: '==' | '!='; value: any }[] {
  const filterClauses: { field: string; operator: '==' | '!='; value: any }[] = [];

  for (const [filterOption, filterValues] of uiFilters.entries()) {
    if (!filterValues || filterValues.length === 0) {
      continue;
    }

    const operator = filterOption.operator === FILTER_OPERATORS.NOT_EQUALS ? '!=' : '==';
    const filterKey = filterOption.id;

    // Handle custom fields (ticketTypeFields.*)
    if (filterKey === 'ticketTypeFields' || filterKey.startsWith('ticketTypeFields.')) {
      for (const filterValue of filterValues) {
        const specificFieldId = filterValue?.id;
        const selectedUIValue = filterValue?.values;

        if (!specificFieldId) {
          continue;
        }

        const field = filterKey === 'ticketTypeFields' ? `ticketTypeFields.${specificFieldId}` : filterKey;
        let value: any = selectedUIValue;

        // Extract IDs from objects if needed
        if (Array.isArray(selectedUIValue)) {
          value = selectedUIValue.map((item) =>
            typeof item === 'object' && item !== null && 'id' in item ? item.id : item,
          );
        } else if (typeof selectedUIValue === 'object' && selectedUIValue !== null && 'id' in selectedUIValue) {
          value = selectedUIValue.id;
        }

        filterClauses.push({ field, operator, value });
      }
    } else {
      // Handle standard filters
      const values = filterValues.map((v) => v.id).filter((id) => id != null);
      if (values.length > 0) {
        filterClauses.push({ field: filterKey, operator, value: values });
      }
    }
  }

  return filterClauses;
}

/**
 * @param savedFilters Raw filter data from a saved report/automation (Record<string, string[]>).
 * @param filterLookups Pre-fetched labels for standard IDs (Record<string, Record<string, string>>).
 * @param componentFilterOptions The FilterOption array defined in the consuming component.
 * @param apiService An instance of ApiService to make necessary backend calls.
 * @returns A Promise resolving to the fully hydrated SelectedFilterMap.
 */
export async function reconstructAndHydrateUiFilters(
  savedFilters: { key: string; operator: SimpleConditionsOperator; values: string[] }[],
  filterLookups: Record<string, Record<string, string>>,
  componentFilterOptions: FilterOption[],
  apiService: ApiService,
  customFieldsService: CustomFieldsService,
): Promise<SelectedFilterMap> {
  const hydratedMap = new Map<FilterOption, FilterValue[]>();

  // Cache for fetched custom field definitions to avoid redundant API calls
  const customFieldDefCache = new Map<string, any>();

  // Process all filters using the populated map
  for (const { key, operator, values: filterIdValues } of savedFilters) {
    const operatorType = (
      [
        SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS,
        SIMPLE_CONDITIONS_OPERATORS.NOT_IN,
        SIMPLE_CONDITIONS_OPERATORS.NOT_EQUALS,
        SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL,
        '!=',
      ] as SimpleConditionsOperator[]
    ).includes(operator)
      ? FILTER_OPERATORS.NOT_EQUALS
      : FILTER_OPERATORS.EQUALS;

    if (key.startsWith('ticketTypeFields.')) {
      // --- Handle Custom Field Filter ---
      const fieldId = key.substring('ticketTypeFields.'.length);

      // Handle null operators specially - they don't need field value hydration
      if (operator === SIMPLE_CONDITIONS_OPERATORS.IS_NULL || operator === SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL) {
        // Fetch custom field definition for the label
        let fieldDefinition: any = customFieldDefCache.get(fieldId);

        if (!fieldDefinition) {
          try {
            fieldDefinition = await customFieldsService.getById(fieldId);
            if (fieldDefinition) {
              customFieldDefCache.set(fieldId, fieldDefinition);
            }
          } catch (error) {
            console.error(`Failed to fetch definition for custom field ID: ${fieldId}`, error);
            continue; // Skip this filter on error
          }
        }

        if (!fieldDefinition) {
          console.warn(`Custom field definition not found for ID: ${fieldId}`);
          continue; // Skip this filter if definition is missing
        }

        const customFieldOption: FilterOption = {
          id: key,
          label: fieldDefinition.name || `Custom Field (${fieldId.substring(0, 6)})`,
          type: 'variable',
          icon: 'flask-outline',
          operator: operatorType,
        };

        const hydratedValues: FilterValue[] = [
          {
            id: fieldId,
            label: fieldDefinition.name || `Custom Field (${fieldId.substring(0, 6)})`,
            values: null, // Null value for null operators
            additionalFields: {
              ...fieldDefinition,
              ticketTypeName: fieldDefinition.ticketType?.name || '',
              // Ensure all necessary fields are copied from the definition
              type: fieldDefinition.type,
              externalSelect: fieldDefinition.externalSelect,
              options: fieldDefinition.options,
            },
          },
        ];

        hydratedMap.set(customFieldOption, hydratedValues);
        continue; // Skip to next filter
      }

      // Fetch custom field definition if not already cached
      let fieldDefinition: any = customFieldDefCache.get(fieldId);

      if (!fieldDefinition) {
        try {
          fieldDefinition = await customFieldsService.getById(fieldId);
          if (fieldDefinition) {
            customFieldDefCache.set(fieldId, fieldDefinition);
          } else {
            console.warn(`Custom field definition not found for ID: ${fieldId}`);
            continue; // Skip this filter if definition is missing
          }
        } catch (error) {
          console.error(`Failed to fetch definition for custom field ID: ${fieldId}`, error);
          continue; // Skip this filter on error
        }
      }

      // Construct a *specific* FilterOption for this custom field
      // Use the fetched definition's name as the label
      // Use the original key (e.g., 'ticketTypeFields.abc') as the ID for the FilterOption
      // This ensures each custom field filter gets its own key in the map
      const customFieldOption: FilterOption = {
        id: key, // Use the full key like 'ticketTypeFields.abc'
        label: fieldDefinition.name || `Custom Field (${fieldId.substring(0, 6)})`, // Use fetched name
        type: 'variable', // Custom fields use variable type
        icon: 'flask-outline', // Default icon, consider making this dynamic if needed
        operator: operatorType,
      };

      let hydratedValues: FilterValue[] = [];

      // --- Logic to handle different custom field types (USER_SELECT, MULTI_SELECT, etc.) ---
      // This logic hydrates the *values* associated with the custom field filter

      if (
        fieldDefinition.type === CUSTOM_FIELD_TYPES.USER_SELECT ||
        fieldDefinition.type === CUSTOM_FIELD_TYPES.MULTI_USER_SELECT
      ) {
        // Hydrate user select
        const fetchedUsers = (
          await Promise.all(
            filterIdValues.map(async (userId: string) => {
              try {
                const lookedUpName = filterLookups[key]?.[userId];
                if (lookedUpName && lookedUpName !== userId) {
                  return { id: userId, name: lookedUpName, email: null };
                } else {
                  const user: any = await apiService.getPromise(`/users/${userId}`);
                  return user ? { id: user.id, name: user.name, email: user.email } : null;
                }
              } catch (error) {
                console.error(`Failed to fetch user ${userId} for ${fieldDefinition.type} filter hydration:`, error);
                return { id: userId, name: `Error User (${userId.substring(0, 6)})`, email: null };
              }
            }),
          )
        ).filter((u) => u != null);
        hydratedValues = [
          {
            id: fieldId,
            label: fieldDefinition.name,
            values: fieldDefinition.type === CUSTOM_FIELD_TYPES.MULTI_USER_SELECT ? fetchedUsers : fetchedUsers[0],
            additionalFields: { ...(fieldDefinition || {}), ticketTypeName: fieldDefinition?.ticketType?.name || '' },
          },
        ];
      } else if (
        fieldDefinition.type === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT ||
        fieldDefinition.type === CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT
      ) {
        // Hydrate user group select
        const fetchedUserGroups = (
          await Promise.all(
            filterIdValues.map(async (userGroupId: string) => {
              try {
                const lookedUpName = filterLookups[key]?.[userGroupId];
                if (lookedUpName && lookedUpName !== userGroupId) {
                  return { id: userGroupId, name: lookedUpName, email: null };
                } else {
                  const userGroup: any = await apiService.getPromise(`/user-groups/${userGroupId}`);
                  return userGroup ? { id: userGroup.id, name: userGroup.name, email: userGroup.email } : null;
                }
              } catch (error) {
                console.error(
                  `Failed to fetch user group ${userGroupId} for ${fieldDefinition.type} filter hydration:`,
                  error,
                );
                return { id: userGroupId, name: `Error User Group (${userGroupId.substring(0, 6)})` };
              }
            }),
          )
        ).filter((u) => u != null);
        hydratedValues = [
          {
            id: fieldId,
            label: fieldDefinition.name,
            values:
              fieldDefinition.type === CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT
                ? fetchedUserGroups
                : fetchedUserGroups[0],
            additionalFields: { ...(fieldDefinition || {}), ticketTypeName: fieldDefinition?.ticketType?.name || '' },
          },
        ];
      } else if (
        fieldDefinition.type === CUSTOM_FIELD_TYPES.MULTI_SELECT ||
        fieldDefinition.type === CUSTOM_FIELD_TYPES.SINGLE_SELECT
      ) {
        // Hydrate single/multi select (internal or external)
        const isExternalSelect = fieldDefinition.externalSelect;
        let selectValueOptions: { id: string; label: string }[] = [];
        let externalOptionsMap = new Map<string, string>();

        if (isExternalSelect) {
          try {
            const externalOptions: { label: string; value: string }[] = await apiService.postPromise(
              `/custom-fields/options`,
              { customFieldId: fieldId, query: '', context: {} },
            );
            externalOptionsMap = new Map(externalOptions.map((opt) => [opt.value, opt.label]));
            selectValueOptions = filterIdValues.map((id: string) => ({
              id: id,
              label: externalOptionsMap.get(id) ?? filterLookups[key]?.[id] ?? `Invalid (${id.substring(0, 6)})`,
            }));
          } catch (error) {
            console.error(`Failed fetching external options for ${fieldDefinition.externalSelect}`, error);
            selectValueOptions = filterIdValues.map((id: string) => ({
              id: id,
              label: filterLookups[key]?.[id] ?? `Error (${id.substring(0, 6)})`,
            }));
          }
        } else {
          // Internal options: use filterLookups with key
          selectValueOptions = filterIdValues.map((id: string) => ({
            id: id,
            label: filterLookups[key]?.[id] ?? id, // Use lookup with key or fallback to ID
          }));
        }

        // Determine the value(s) to store in the FilterValue
        let uiValueForStorage: any;
        if (fieldDefinition.type === CUSTOM_FIELD_TYPES.MULTI_SELECT) {
          if (isExternalSelect) {
            // External multi-select: Store the array of objects {id, label}
            uiValueForStorage = selectValueOptions;
          } else {
            // Internal multi-select: Store only the array of IDs (strings)
            uiValueForStorage = selectValueOptions.map((opt) => opt.id);
          }
        } else {
          // SINGLE_SELECT
          uiValueForStorage = selectValueOptions[0] ?? null; // Store the single option object or null
          // If internal select with simple string options, store the string ID instead of object
          if (!isExternalSelect && fieldDefinition.options?.every((opt: any) => typeof opt === 'string')) {
            uiValueForStorage = selectValueOptions[0]?.id ?? null;
          }
        }

        hydratedValues = [
          {
            id: fieldId, // Field ID for FilterValue ID
            label: fieldDefinition.name, // Field name for FilterValue label
            values: uiValueForStorage, // Store the processed value/options
            additionalFields: { ...(fieldDefinition || {}), ticketTypeName: fieldDefinition?.ticketType?.name || '' },
          },
        ];
      } else {
        // Handle other types (TEXT, CHECKBOX, DATE) - assumes single value from backend
        for (const filterValue of filterIdValues) {
          let uiValueForStorage: any = filterValue; // Default to raw value

          // Checkbox/Date don't need lookups for their values
          if (fieldDefinition.type === CUSTOM_FIELD_TYPES.CHECKBOX) {
            uiValueForStorage = String(filterValue).toLowerCase() === 'true';
          } else if (fieldDefinition.type === CUSTOM_FIELD_TYPES.DATE) {
            // Keep as ISO string, UI component handles formatting
            uiValueForStorage = filterValue;
          } else if (filterValue === CURRENT_USER_SENTINEL) {
            // Handle sentinel display for custom user fields if necessary
            // This case might need refinement depending on how custom user fields are handled
            // For now, assume it's handled by the standard filter logic below
            console.warn('CURRENT_USER_SENTINEL found in custom field value, needs review.');
            continue; // Or handle appropriately
          }
          // TEXT fields use the filterValue directly

          hydratedValues.push({
            id: fieldId, // Field ID for FilterValue ID
            label: fieldDefinition.name, // Field name for FilterValue label
            values: uiValueForStorage, // Store the processed value
            additionalFields: { ...(fieldDefinition || {}), ticketTypeName: fieldDefinition?.ticketType?.name || '' },
          });
        }
      }

      // Add the hydrated values to the map using the *specific* customFieldOption as the key
      if (hydratedValues.length > 0) {
        hydratedMap.set(customFieldOption, hydratedValues);
      }
    } else {
      // --- Handle Standard Filter ---
      const baseFilterOption = componentFilterOptions.find((o) => o.id === key);

      if (baseFilterOption) {
        // Create a deep copy to avoid sharing references between branches
        const standardFilterOption = {
          ...baseFilterOption,
          operator: operatorType,
        };

        // Special handling for date filters with relative date JSON values
        if (baseFilterOption.type === 'date' && operator === 'in') {
          const values: FilterValue[] = (filterIdValues || []).map((savedValueId) => {
            try {
              // Try to parse as relative date JSON
              const parsedDate = JSON.parse(savedValueId);
              if (parsedDate.key && DATE_OPTION_LABELS[parsedDate.key]) {
                return {
                  id: savedValueId, // Keep the JSON string as the ID
                  label: DATE_OPTION_LABELS[parsedDate.key], // Use the proper label
                };
              }
            } catch (error) {
              // Not a JSON string, handle as regular value
              console.warn('Failed to parse date filter JSON:', savedValueId);
            }

            // Fallback for non-JSON date values
            return {
              id: savedValueId,
              label: savedValueId,
            };
          });

          if (values.length > 0) {
            hydratedMap.set(standardFilterOption, values);
          }
          continue; // Skip to next filter
        }

        const lookupMap = filterLookups[key];
        const fallbackLabel = (savedValueId: string) =>
          ['customerId', 'accountId', 'slackChannelId'].includes(key) ? PRIVATE_CHANNEL_NAME : savedValueId;

        if (!lookupMap) {
          console.warn(`Lookup map not found for standard filter key: ${key}`);
          // Attempt to use the raw values if lookup fails
          const rawValues: FilterValue[] = (filterIdValues || []).map((savedValueId) => ({
            id: savedValueId,
            label: fallbackLabel(savedValueId), // Fallback to using the ID as label
          }));

          if (rawValues.length > 0) {
            hydratedMap.set(standardFilterOption, rawValues);
          }

          continue; // Skip to next filter
        }
        // Use the lookup map
        const values: FilterValue[] = (filterIdValues || []).map((savedValueId) => ({
          id: savedValueId,
          label:
            savedValueId === CURRENT_USER_SENTINEL
              ? 'Current User'
              : lookupMap[savedValueId] ?? fallbackLabel(savedValueId), // Use lookup or fallback to ID, handle sentinel
        }));

        if (values.length > 0) {
          hydratedMap.set(standardFilterOption, values);
        }
      } else {
        console.warn(`Standard filter option not found for key: ${key}`);
      }
    }
  }

  return hydratedMap;
}

/**
 * Transforms the backend SimpleConditionsWrapper format into the flat
 * format expected by reconstructAndHydrateUiFilters.
 */
export function transformConditionsToFlatRecord(
  conditions?: SimpleConditionsWrapper,
): { key: string; operator: SimpleConditionsOperator; values: string[] }[] {
  const flatFilters: { key: string; operator: SimpleConditionsOperator; values: string[] }[] = [];
  const filter: any = conditions?.simple;

  const SUPPORTED_OPERATORS = [
    SIMPLE_CONDITIONS_OPERATORS.IN,
    SIMPLE_CONDITIONS_OPERATORS.CONTAINS,
    SIMPLE_CONDITIONS_OPERATORS.EQUALS,
    SIMPLE_CONDITIONS_OPERATORS.NOT_EQUALS,
    SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS,
    SIMPLE_CONDITIONS_OPERATORS.NOT_IN,
    SIMPLE_CONDITIONS_OPERATORS.IS_NULL,
    SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL,
  ];

  if (filter?.and?.length) {
    filter.and.forEach((clause) => {
      if (!SUPPORTED_OPERATORS.includes(clause.operator)) {
        return;
      }

      const [valuePartsPrefix, ...valuePartsSuffix] = clause.leftValueFrom.split('.');
      let filterKey: string;

      // Check if it's a custom field key
      const customFieldMatch = clause.leftValueFrom.match(/^conversation\.ticketTypeFields\['(.+?)'\]$/);
      if (customFieldMatch) {
        filterKey = `ticketTypeFields.${customFieldMatch[1]}`;
      } else {
        // Otherwise, treat as a standard field key
        filterKey = valuePartsSuffix.join('.') || valuePartsPrefix;
      }

      // Handle null operators that don't have rightValue
      if (
        clause.operator === SIMPLE_CONDITIONS_OPERATORS.IS_NULL ||
        clause.operator === SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL
      ) {
        flatFilters.push({
          key: filterKey,
          operator: clause.operator,
          values: ['null'], // Use 'null' as a placeholder value for UI hydration
        });
      } else {
        // Ensure rightValue is always an array for consistency
        const values = Array.isArray(clause.rightValue) ? clause.rightValue : [clause.rightValue];

        if (values.length > 0) {
          flatFilters.push({
            key: filterKey,
            operator: clause.operator,
            values,
          });
        }
      }
    });
  }

  return flatFilters;
}

/**
 * Collects lookup IDs from conditions for fetching entity labels
 */
export function collectLookupIdsFromConditions(
  conditions?: SimpleConditionsWrapper,
  lookupIds: Record<string, string[]> = {},
): Record<string, string[]> {
  const filter: any = conditions?.simple;

  if (filter?.and?.length) {
    filter.and.forEach((clause: SimpleConditionsLeaf) => {
      const [valuePartsPrefix, ...valuePartsSuffix] = clause.leftValueFrom.split('.');
      const valueParts = [valuePartsPrefix, valuePartsSuffix.join('.')];

      if (valueParts[1]?.startsWith('ticketTypeFields')) {
        // Custom field
        const fieldIdMatch = clause.leftValueFrom.match(/ticketTypeFields\['(.+?)'\]/);
        const fieldId = fieldIdMatch?.[1];
        if (fieldId) {
          if (!lookupIds['customFieldId']) {
            lookupIds['customFieldId'] = [];
          }
          lookupIds['customFieldId'].push(fieldId);
        }
      } else {
        // Standard field
        const filterTypeId = valueParts[1] ?? valueParts[0];
        const values = clause.rightValue;

        if (values && Array.isArray(values) && values.length > 0) {
          if (!lookupIds[filterTypeId]) {
            lookupIds[filterTypeId] = [];
          }
          lookupIds[filterTypeId].push(...values.map(String));
        }
      }
    });
  }

  return lookupIds;
}

/**
 * Converts a value to the appropriate type for database storage
 */
export function convertValueForStorage(value: any, optionId: string): string | number | boolean {
  return optionId === 'priority' ? Number(value) : value;
}

/**
 * Determines the appropriate operator for custom fields based on field type
 */
export function getCustomFieldOperator(
  option: FilterOption,
  fieldType: any,
): (typeof SIMPLE_CONDITIONS_OPERATORS)[keyof typeof SIMPLE_CONDITIONS_OPERATORS] {
  if (
    [
      CUSTOM_FIELD_TYPES.SINGLE_SELECT,
      CUSTOM_FIELD_TYPES.USER_SELECT,
      CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
      CUSTOM_FIELD_TYPES.DATE,
      CUSTOM_FIELD_TYPES.CHECKBOX,
    ].includes(fieldType)
  ) {
    return option.operator === FILTER_OPERATORS.NOT_EQUALS
      ? SIMPLE_CONDITIONS_OPERATORS.NOT_EQUALS
      : SIMPLE_CONDITIONS_OPERATORS.EQUALS;
  }

  return option.operator === FILTER_OPERATORS.NOT_EQUALS
    ? SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS
    : SIMPLE_CONDITIONS_OPERATORS.CONTAINS;
}

/**
 * Processes custom field values for storage
 */
export function processCustomFieldValue(value: any, fieldType: string, isExternalSelect: boolean): any {
  // Handle null values
  if (value.values === null) {
    return null;
  }

  if (isExternalSelect) {
    if (fieldType === CUSTOM_FIELD_TYPES.SINGLE_SELECT) {
      return value.values?.id ?? null;
    }
    if (fieldType === CUSTOM_FIELD_TYPES.MULTI_SELECT) {
      return Array.isArray(value.values) ? value.values.map((v) => (v === null ? null : v.id)) : [];
    }
  }

  if (fieldType === CUSTOM_FIELD_TYPES.USER_SELECT || fieldType === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT) {
    return value.values?.id ?? null;
  }
  if (fieldType === CUSTOM_FIELD_TYPES.MULTI_USER_SELECT || fieldType === CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT) {
    return Array.isArray(value.values) ? value.values.map((v) => (v === null ? null : v.id)) : [];
  }
  return value.values;
}

/**
 * Creates a condition for a custom field
 */
export function createCustomFieldCondition(option: FilterOption, value: any): SimpleConditions {
  const fieldType = value.additionalFields?.type;
  const isExternalSelect = value.additionalFields?.externalSelect;
  const processedValue = processCustomFieldValue(value, fieldType, isExternalSelect);

  // Use explicit isNull/isNotNull operators for null values
  if (processedValue === null) {
    return {
      leftValueFrom: `conversation.ticketTypeFields['${value.id}']`,
      operator:
        option.operator === FILTER_OPERATORS.NOT_EQUALS
          ? SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL
          : SIMPLE_CONDITIONS_OPERATORS.IS_NULL,
    };
  }

  // For arrays containing null values, we need to handle mixed scenarios
  if (Array.isArray(processedValue) && processedValue.includes(null)) {
    // If the array contains both null and non-null values, use the original logic
    // This will be handled by the backend condition evaluation
    const nonNullValues = processedValue.filter((v) => v !== null);
    if (nonNullValues.length === 0) {
      // Array contains only null values
      return {
        leftValueFrom: `conversation.ticketTypeFields['${value.id}']`,
        operator:
          option.operator === FILTER_OPERATORS.NOT_EQUALS
            ? SIMPLE_CONDITIONS_OPERATORS.IS_NOT_NULL
            : SIMPLE_CONDITIONS_OPERATORS.IS_NULL,
      };
    }
  }

  return {
    leftValueFrom: `conversation.ticketTypeFields['${value.id}']`,
    operator: getCustomFieldOperator(option, fieldType),
    rightValue: processedValue,
  };
}

/**
 * Creates a condition for a standard field
 */
export function createStandardFieldCondition(
  option: FilterOption,
  valueIds: (string | number | boolean)[],
): SimpleConditions {
  return {
    leftValueFrom:
      option.id === 'isWorkingTime'
        ? 'isWorkingTime'
        : option.id === 'isAutoresponse'
          ? 'message.isAutoresponse'
          : `conversation.${option.id}`,
    operator: ['tagId', 'customerTagId', 'accountTagId'].includes(option.id)
      ? option.operator === FILTER_OPERATORS.NOT_EQUALS
        ? SIMPLE_CONDITIONS_OPERATORS.NOT_CONTAINS
        : SIMPLE_CONDITIONS_OPERATORS.CONTAINS
      : option.operator === FILTER_OPERATORS.NOT_EQUALS
        ? SIMPLE_CONDITIONS_OPERATORS.NOT_IN
        : SIMPLE_CONDITIONS_OPERATORS.IN,
    rightValue: valueIds,
  };
}

/**
 * Converts UI filter map to database conditions format
 */
export function filterMapToConditions(
  filterMap: Map<FilterOption, { id: string | boolean; label: string; values?: any; additionalFields?: any }[]>,
): SimpleConditionsWrapper {
  const andedFilters: SimpleConditions[] = [];

  for (const [option, values] of filterMap.entries()) {
    // Convert values to appropriate types
    const valueIds = values.map((v) => convertValueForStorage(v.id, option.id));

    if (!valueIds.length) continue;

    if (option.id === 'customFieldId' || option.id.startsWith('ticketTypeFields')) {
      // Handle custom fields
      const customFieldConditions = values.map((value) => createCustomFieldCondition(option, value));
      andedFilters.push(...customFieldConditions);
    } else {
      // Handle standard fields
      andedFilters.push(createStandardFieldCondition(option, valueIds));
    }
  }

  return {
    type: CONDITIONS_TYPES.SIMPLE,
    simple: andedFilters.length ? { and: andedFilters } : {},
  };
}
